import React, { useEffect, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useDispatch } from 'react-redux'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Box, Button, Card, CardMedia, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useShop } from 'src/Libs/shop/hooks'
import { ProductResponse } from '../../models'
import { addRetrofitBattery } from 'src/State/ShopSystemConfiguration/ShopSystemConfigurationAction'
import { ShopMainSkeleton } from '../Skeletons'
import { currencyFormatter } from '../../utils/formatter'
import RetrofitBatteryDetails from './RetrofitBattery/RetrofitBatteryDetails'
import { getImageUrlBySize } from '../../utils'
import { ShopImageThumnbailSizes } from '../../types'
import { QuantityAvailabilityIndicator } from '../ShopProduct'
import ButtonQuantity from '../ShopProduct/ButtonQuantity'

export interface RetrofitBatteryModalProps {
  open: boolean
  batteryId: string
  min: number
  max: number
  onClose: () => void
  place: number
}

const useStyles = makeStyles()(theme => ({
  button: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.only('xs')]: {
      margin: theme.spacing(1),
    },
  },
  cardWrapper: {
    marginTop: theme.spacing(2),
    display: 'grid',
    alignItems: 'strech',
    justifyContent: 'center',
    justifyItems: 'center',
    gap: theme.spacing(4),
    gridAutoRows: '1fr',
    gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(auto-fit, 270px)',
      justifyContent: 'center',
    },
  },
  productImage: {
    margin: 0,
    padding: 0,
    objectFit: 'fill',
    width: '100%',
    maxWidth: '100%',
    height: '220px',
    marginBottom: theme.spacing(2),
  },
  product: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '285px',
    maxWidth: '285px',
    flexShrink: 0,
    margin: theme.spacing(2),
    padding: 0,
    paddingBottom: theme.spacing(2),
  },
  containerTextWrapper: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  priceContainer: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  flexPrice: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  modalButtonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const RetrofitBatteryModal: React.FC<RetrofitBatteryModalProps> = ({ open, place, onClose, batteryId, min, max }): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { getShopProduct } = useShop()
  const [productData, setProductData] = useState<ProductResponse | null>()
  const dispatch = useDispatch()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  const [selected, setSelected] = useState<string | null>(null)
  const [quantity, setQuantity] = useState<number>(0)

  const [moreInfoProduct, setMoreInfoProduct] = useState<boolean>(false)

  const modalActions = {
    primary: {
      name: t('shop.retrofit-battery.add-to-system'),
      onClick: () => {
        quantity !== 0 && selected != null && dispatch(addRetrofitBattery(selected, quantity))
        onClose()
      },
    },
    secondary: { name: t('shop.retrofit-battery.cancel') },
  }

  const unitPrice = productData?.product.calculatedPrice.unitPrice ?? 0
  const price = productData?.product?.calculatedPrice?.listPrice?.price ?? 0
  const productAvailability = productData?.product?.extensions?.e3dcAvailability

  useEffect(() => {
    getShopProduct(batteryId)
      .then(result => {
        setProductData(result)
      })
      .finally(() => {
        setIsLoading(false)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batteryId])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  const handleUserExitDetails = (): void => {
    setMoreInfoProduct(false)
  }

  const handleQuantityChange = (newQuantity: number): void => {
    if (newQuantity >= min && newQuantity <= max) {
      setQuantity(newQuantity)
    } else {
      setSelected(null)
    }
  }

  const isProductSelected = productData && productData.product.id !== selected
  const productImageUrl = getImageUrlBySize(productData?.product?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Small)

  return (
    <Dialog
      maxWidth="sm"
      title={!moreInfoProduct ? t('shop.retrofit-battery.title') : undefined}
      actions={modalActions}
      onClose={onClose}
      place={place}
    >
      {isLoading ? (
        <Box marginTop={3}>
          <ShopMainSkeleton shopSkeletonType="product" />
        </Box>
      ) : (
        <>
          {!moreInfoProduct ? (
            <Box className={classes.cardWrapper}>
              {productData && (
                <Card className={classes.product}>
                  <Box sx={{ flex: '1' }}>
                    <CardMedia className={classes.productImage} component="img" image={productImageUrl} alt="Product" />
                    <Box className={classes.containerTextWrapper}>
                      <Typography align="left" variant="h3">
                        {productData.product.translated?.name}
                        <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
                      </Typography>
                      <Box className={classes.priceContainer}>
                        <Box className={classes.flexPrice}>
                          <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
                            {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
                          </Typography>
                          {unitPrice < price && (
                            <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                              {currencyFormatter(price)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box className={classes.modalButtonContainer}>
                    <Button
                      className={classes.button}
                      size="large"
                      color="inherit"
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        setMoreInfoProduct(true)
                      }}
                    >
                      {t('shop.common.more-info')}
                    </Button>

                    {isProductSelected ? (
                      <Button
                        className={classes.button}
                        color="primary"
                        variant="contained"
                        fullWidth
                        onClick={() => {
                          setSelected(productData.product.id)
                          setQuantity(min)
                        }}
                      >
                        {t('shop.common.select')}
                      </Button>
                    ) : (
                      <ButtonQuantity
                        quantity={quantity}
                        disabled
                        maxQuantity={max}
                        onQuantityChange={quantity => handleQuantityChange(quantity)}
                        onQuantityIncrease={() => handleQuantityChange(quantity + 1)}
                        onQuantityDecrease={() => handleQuantityChange(quantity - 1)}
                      />
                    )}
                  </Box>
                </Card>
              )}
            </Box>
          ) : (
            <Box>{productData && <RetrofitBatteryDetails detailsData={productData} onUserExitDetails={handleUserExitDetails} />}</Box>
          )}
        </>
      )}
    </Dialog>
  )
}

export default RetrofitBatteryModal
